import { z } from 'zod';
import { guideStatusEnum } from './enums.ts';

// ── Build-time content (used by the seed-pipeline manifest builder) ──
//
// Markdown source files at `content/guides/**` declare frontmatter
// matching this schema. The manifest builder validates each file at
// `predev`/`prebuild` time; the resulting manifest seeds new accounts.

export const guideFrontmatter = z.object({
  title: z.string().min(1),
  slug: z.string().min(1),
  domain: z.string().min(1),
  status: guideStatusEnum.default('active'),
  summary: z.string().optional(),
  tags: z.array(z.string()).default([]),
  updatedAt: z.string().min(1),
});
export type GuideFrontmatter = z.infer<typeof guideFrontmatter>;

export interface GuideEntry extends GuideFrontmatter {
  body: string;
  sourcePath: string;
}

export interface GuideMeta extends GuideFrontmatter {
  sourcePath: string;
}

/**
 * Drop the body so the wire response from `GET /api/guides` is a
 * compact list of metadata; full bodies come from `GET /api/guides/:slug`.
 */
export function toMeta(entry: GuideEntry): GuideMeta {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { body, ...rest } = entry;
  return rest;
}

// ── Runtime API inputs (DB-backed user-authored guides) ──────────────

export const guideSlug = z
  .string()
  .min(2)
  .max(80)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'kebab-case lowercase only');

export const createGuideInput = z.object({
  slug: guideSlug,
  title: z.string().min(2).max(200),
  domainId: z.string().min(1),
  bodyMd: z.string().max(200_000), // 200 KB cap; protects against paste accidents
  lastReviewed: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected ISO yyyy-mm-dd')
    .optional()
    .nullable(),
});
export type CreateGuideInput = z.infer<typeof createGuideInput>;

// Patch is structurally identical (full-replace), defined as a clone
// to leave room for patch-specific tightening later.
export const patchGuideInput = createGuideInput;
export type PatchGuideInput = z.infer<typeof patchGuideInput>;
